import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import {
	Flex,
	Tabs,
	Mark,
	Image,
	Title,
	Stack,
	Box,
	List,
	useMatches,
} from "@mantine/core";
import { motion } from "framer-motion";
import imgLearn from "../../assets/illust/landingpage/learn.png";
import imgWrite from "../../assets/illust/landingpage/write.png";
import imgReview from "../../assets/illust/landingpage/review.png";
import screenWrite from "../../assets/screenshots/screen_review.jpg";
import { useTranslation } from "react-i18next";
import { useResponsiveOrder } from "../../hooks/useResponsiveOrder";
import iconThreads from "../../assets/icon/social/icon_social_threads.svg";
import ReactPlayer from "react-player";

const videoList = [
	"https://firebasestorage.googleapis.com/v0/b/data-driven-design-d2418.appspot.com/o/my%20threads%20insight%2Fdemo%2Fc1_final.mp4?alt=media&token=0ddfe9e7-9460-4501-8526-03884cd1c408",
	"https://firebasestorage.googleapis.com/v0/b/data-driven-design-d2418.appspot.com/o/my%20threads%20insight%2Fdemo%2Fc2_final.mp4?alt=media&token=351f1de5-1f80-46c0-a8b3-40fa427f0fb5",
];

const LoginTabsSection = ({ svgRef }) => {
	const { i18n, t } = useTranslation();
	const responsiveOrder = useResponsiveOrder(3, 2, 2);
	const Bold = styled(Mark)`
		font-weight: 900;
		background-color: transparent !important;
	`;

	const tabs = ["Review Past", "Learn Impact", "Write Niche"];
	const tabsTitles = [t("landing.tab1"), t("landing.tab2"), t("landing.tab3")];
	const contentList = [
		{
			title: "Review Past",
			description: {
				en: (
					<Title fw={400} order={responsiveOrder}>
						Discover your <Bold>Potentiality</Bold>
						<br />
						Through your <Bold>Past</Bold> threads.
					</Title>
				),
				ko: (
					<Title fw={400} order={responsiveOrder}>
						이전 <Bold>스레드</Bold>를 읽어보고
						<br />
						나의 <Bold>잠재력</Bold>을 발견하세요.
					</Title>
				),
			},
			img: imgReview,
			screenshot: videoList[0],
		},
		{
			title: "Learn Impact",
			description: {
				en: (
					<Title fw={400} order={responsiveOrder}>
						Learning your <Bold>Impact</Bold>
						<br />
						And Get New <Bold>Insight</Bold>.
					</Title>
				),
				ko: (
					<Title fw={400} order={responsiveOrder}>
						나의 <Bold>영향력</Bold>을 분석하고
						<br />
						새로운 <Bold>인사이트</Bold>를 얻으세요.
					</Title>
				),
			},
			img: imgLearn,
			screenshot: videoList[1],
		},
		{
			title: "Write Niche",
			description: {
				en: (
					<Title fw={400} order={responsiveOrder}>
						Write <Bold>Next</Bold> Threads
						<br />
						Grow <Bold>Niches</Bold> For Your Brand.
					</Title>
				),
				ko: (
					<Title fw={400} order={responsiveOrder}>
						어울리는 <Bold>주제를</Bold> 정하고
						<br />
						나만의 <Bold>니치</Bold>를 선점하세요.
					</Title>
				),
			},
			img: imgWrite,
			screenshot: screenWrite,
		},
	];

	const tabSize = useMatches({
		base: "xs",
		sm: "xs",
		md: "md",
		lg: "md",
		xl: "md",
	});

	const sectionGap = useMatches({
		base: 32,
		md: 48,
	});

	const contentGap = useMatches({
		sm: 16,
		md: 32,
	});

	// isPageLoaded의 초기값을 true로 설정
	const [isPageLoaded, setIsPageLoaded] = useState(true);
	// 현재 활성화된 탭 추적을 위한 상태 추가
	const [activeTab, setActiveTab] = useState(tabs[0]);

	useEffect(() => {
		if (document.readyState === 'interactive' || document.readyState === 'complete') {
			setIsPageLoaded(true);
		} else {
			document.addEventListener('DOMContentLoaded', () => setIsPageLoaded(true));
		}

		return () => {
			document.removeEventListener('DOMContentLoaded', () => setIsPageLoaded(true));
		};
	}, []);

	const containerVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				delayChildren: 1.4,
				staggerChildren: 0.3
			}
		}
	};
	
	const itemVariants = {
		hidden: { opacity: 0, y: 20 },
		visible: { 
			opacity: 1, 
			y: 0,
			transition: { duration: 0.5 }
		}
	};

	const tabsContainerVariants = {
		hidden: { opacity: 0, y: 20 },
		visible: { 
			opacity: 1, 
			y: 0,
			transition: { 
				duration: 0.6,
				delay: 1.4
			}
		}
	};
	
	const contentVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				delay: 1.8,
				duration: 0.6
			}
		}
	};

	return (
		<motion.div
			variants={containerVariants}
			initial="hidden"
			animate="visible"
			style={{ width: "100%", position: "relative" }}
		>
			<Tabs
				w={{ base: "calc(100% - 2rem)", md: "100%" }}
				variant="none"
				defaultValue={tabs[0]}
				onChange={(value) => setActiveTab(value)}
			>
				<Stack gap={sectionGap} w={"100%"}>
					<motion.div variants={tabsContainerVariants} style={{ width: "100%" }}>
						<Flex
							w={"100%"}
							p={{ base: "0.5rem", md: "1rem" }}
							style={{
								border: "0.5px solid var(--mantine-color-gray-6)",
								borderRadius: "50px",
								position: "relative",
								backgroundColor: "var(--mantine-color-gray-1)",
							}}
						>
							<svg
								ref={svgRef}
								style={{
									position: "absolute",
									top: "-4px",
									left: "-4px",
									pointerEvents: "none",
								}}
							/>
							<Tabs.List w={"100%"}>
								<Flex w={"100%"} align={"center"} justify={"center"}>
									{tabs.map((title, index) => (
										<Flex
											key={index}
											w={"calc(100% / 3)"}
											align={"center"}
											justify={"center"}
										>
											{index > 0 && <span>•</span>}
											<Tabs.Tab
												size={tabSize}
												w={"calc(100% - 0.5rem)"}
												value={title}
											>
												{tabsTitles[index]}
											</Tabs.Tab>
										</Flex>
									))}
								</Flex>
							</Tabs.List>
						</Flex>
					</motion.div>

					{tabs.map((title, index) => (
						<Tabs.Panel key={title} value={title} w={"100%"}>
							<motion.div 
								variants={contentVariants}
								initial="hidden"
								animate="visible"
								style={{ width: "100%" }}
							>
								<Flex
									direction={"column"}
									gap={contentGap}
									w={"100%"}
									align={"center"}
									justify={"center"}
									style={{ overflow: "hidden", width: "100%" }}
								>
									<Flex
										w={"100%"}
										direction={{ base: "column", sm: "row" }}
										justify={"space-between"}
										align={"center"}
									>
										<motion.div variants={itemVariants}>
											<Stack gap={contentGap}>
												{
													contentList.find((content) => content.title === title)
														?.description[i18n.language]
												}
												<List
													ta={"left"}
													spacing="8"
													center
													align="center"
													icon={<Image src={iconThreads} w={16} h={16} />}
												>
													<List.Item fh={1} fz={{ base: "lg", sm: "xl" }}>
														{t(`landing.tab${index + 1}Exp1`)}
													</List.Item>
													<List.Item fh={1} fz={{ base: "lg", sm: "xl" }}>
														{t(`landing.tab${index + 1}Exp2`)}
													</List.Item>
												</List>
											</Stack>
										</motion.div>
										<motion.div variants={itemVariants}>
											<Box>
												<Image
													h={{ base: "224px", md: "248px" }}
													w={"auto"}
													src={
														contentList.find((content) => content.title === title)
															?.img
													}
												/>
											</Box>
										</motion.div>
									</Flex>
									{isPageLoaded && (
										<motion.div 
											variants={itemVariants}
											style={{ width: "100%" }}
										>
											<Flex
												align="center"
												justify="center"
												w={"100%"}
												style={{ overflow: "hidden", borderRadius: "8px" }}
											>
												{index === tabs.length - 1 ? (
													<Flex
														align="center"
														justify="center"
														w="100%"
														h="300px"
														style={{
															background: "linear-gradient(45deg, var(--mantine-color-gray-1), var(--mantine-color-gray-4))",
															backgroundSize: "200% 200%",
															borderRadius: "8px",
															color: "white",
															fontSize: "24px",
															fontWeight: "bold",
															animation: "gradientAnimation 5s ease infinite"
														}}
													>
														{t(`landing.tab3Exp3`)}
													</Flex>
												) : (
													<Box
														style={{
															width: "100%",
															height: "496px",
															position: "relative",
															borderRadius: "8px",
															overflow: "hidden",
															backgroundColor: "var(--mantine-color-gray-1)",
														}}
													>
														<ReactPlayer
															key={`video-${index}-${Date.now()}`}
															url={
																contentList.find((content) => content.title === title)
																	?.screenshot
															}
															width="100%"
															height="100%"
															controls={true}
															playing={activeTab === title}
															loop={true}
															muted={true}
															playsinline={true}
															onReady={() => console.log(`Video ${index} ready!`)}
															onError={(e) => console.error(`Video ${index} error:`, e)}
														/>
													</Box>
												)}
											</Flex>
										</motion.div>
									)}
								</Flex>
							</motion.div>
						</Tabs.Panel>
					))}
				</Stack>
			</Tabs>
		</motion.div>
	);
};

export default LoginTabsSection;
